/**
 * Engine behavior selector — ordered rule list + commit hysteresis + forced
 * pre-emption. Genre-neutral: Ctx/Decision are opaque generics; rules are
 * evaluated in array order (priority tiers = array layout, domain's choice).
 * Domain owns rule predicates, decisions, and what "forced" means (orders).
 */

export type BehaviorRule<Ctx, Decision> = {
  id: string;
  /** <= 0 → rule does not apply this evaluation. Positive = applicable. */
  score(ctx: Ctx): number;
  decide(ctx: Ctx): Decision;
};

export type BehaviorState = {
  activeId: string | null;
  committedUntil: number;
};

export type BehaviorSelectKnobs = {
  /** Minimum seconds a newly selected rule is kept (thrash damping). */
  commitSec: number;
  /**
   * While committed, an earlier-ordered (higher priority) rule may still
   * pre-empt; a later-ordered rule may not. After commit expiry, the first
   * applicable rule in order wins.
   */
  switchMargin?: number;
};

export function createBehaviorState(): BehaviorState {
  return { activeId: null, committedUntil: 0 };
}

/**
 * Pick the active rule. Order = priority (index 0 highest). Hysteresis:
 * the active rule is kept until `committedUntil` unless a strictly
 * higher-priority rule becomes applicable (reflexes/orders can always
 * pre-empt lower tiers by being placed earlier in the array).
 * `forcedId` bypasses everything (still must exist and apply).
 */
export function selectBehavior<Ctx, D>(
  rules: readonly BehaviorRule<Ctx, D>[],
  ctx: Ctx,
  state: BehaviorState,
  time: number,
  knobs: BehaviorSelectKnobs,
  forcedId?: string | null,
): { id: string; decision: D } | null {
  if (forcedId) {
    const forced = rules.find((r) => r.id === forcedId);
    if (forced && forced.score(ctx) > 0) {
      commit(state, forced.id, time, knobs);
      return { id: forced.id, decision: forced.decide(ctx) };
    }
  }

  const activeIdx = state.activeId
    ? rules.findIndex((r) => r.id === state.activeId)
    : -1;
  const committed = activeIdx >= 0 && time < state.committedUntil;

  for (let i = 0; i < rules.length; i++) {
    const rule = rules[i]!;
    if (committed && i > activeIdx) break; // lower priority cannot interrupt
    if (rule.score(ctx) <= 0) continue;
    if (rule.id !== state.activeId) commit(state, rule.id, time, knobs);
    return { id: rule.id, decision: rule.decide(ctx) };
  }

  // Committed rule no longer applies and nothing above it does: fall through
  // to the rest of the list once.
  if (committed) {
    for (let i = activeIdx + 1; i < rules.length; i++) {
      const rule = rules[i]!;
      if (rule.score(ctx) <= 0) continue;
      commit(state, rule.id, time, knobs);
      return { id: rule.id, decision: rule.decide(ctx) };
    }
  }

  state.activeId = null;
  return null;
}

function commit(
  state: BehaviorState,
  id: string,
  time: number,
  knobs: BehaviorSelectKnobs,
): void {
  state.activeId = id;
  state.committedUntil = time + Math.max(0, knobs.commitSec);
}
